/**
 * WooCommerce Store API helpers for Cart operations.
 *
 * The Store API requires a Nonce header for all mutating requests.
 * The nonce is provided via wp_add_inline_script in functions.php
 * and refreshed from response headers after each request.
 */

const BASE = "/wp-json/wc/store/v1/cart";

function getNonce() {
	return window.wcBlocksMiddlewareConfig?.storeApiNonce || "";
}

function refreshNonce(res) {
	const newNonce = res.headers.get("Nonce");
	if (newNonce && window.wcBlocksMiddlewareConfig) {
		window.wcBlocksMiddlewareConfig.storeApiNonce = newNonce;
	}
}

function reqHeaders() {
	return {
		"Content-Type": "application/json",
		Nonce: getNonce(),
	};
}

async function storeApiFetch(url, options = {}) {
	const res = await fetch(url, {
		...options,
		headers: { ...reqHeaders(), ...options.headers },
	});
	refreshNonce(res);

	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		const error = new Error(data.message || `Request failed (${res.status})`);
		// Carried so callers can react to *which* Store API error this was —
		// see removeItemSafe(), which treats "already gone" as success rather
		// than surfacing a scary error for a removal that in fact worked.
		error.code = data.code || "";
		error.data = data.data || null;
		throw error;
	}

	return res.json();
}

export function getCart() {
	return storeApiFetch(BASE);
}

export function updateItemQty(key, quantity) {
	return storeApiFetch(`${BASE}/update-item`, {
		method: "POST",
		body: JSON.stringify({ key, quantity }),
	});
}

export function removeItem(key) {
	return storeApiFetch(`${BASE}/remove-item`, {
		method: "POST",
		body: JSON.stringify({ key }),
	});
}

/**
 * Remove an item, tolerating a cart that already dropped this key on the
 * server. A configured racket build (see BuildGroup::reconcile()) removes
 * its string/overgrip lines automatically the moment its racket line is
 * removed — so a caller looping over a snapshot of keys (clearCart(),
 * "remove build") can legitimately ask to remove a key that's already gone.
 * The Store API answers that with 409 woocommerce_rest_cart_invalid_key,
 * which is not a failure here: the end state the caller wanted (this key
 * not in the cart) is already true, so resolve with the fresh cart the
 * error itself carries instead of throwing.
 */
export async function removeItemSafe(key) {
	try {
		return await removeItem(key);
	} catch (err) {
		if (err.code === "woocommerce_rest_cart_invalid_key") {
			return err.data?.cart || getCart();
		}
		throw err;
	}
}

export function applyCoupon(code) {
	return storeApiFetch(`${BASE}/apply-coupon`, {
		method: "POST",
		body: JSON.stringify({ code }),
	});
}

export function removeCoupon(code) {
	return storeApiFetch(`${BASE}/remove-coupon`, {
		method: "POST",
		body: JSON.stringify({ code }),
	});
}

export async function clearCart(items) {
	let cart = null;
	for (const item of items) {
		// A prior removal in this same loop may have already cascaded this
		// key away (e.g. it was a build's string, removed when its racket
		// was); skip it instead of asking the server to remove it twice.
		if (cart && !cart.items.some((line) => line.key === item.key)) {
			continue;
		}
		cart = await removeItemSafe(item.key);
	}
	return cart || getCart();
}
